import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type { ServiceOffering } from '@freelancer/datastore/collections';
import { AnimationBehavior } from '@freelancer/directives';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { Observable } from 'rxjs';
import type { Service } from './ai-services.types';
import { ServiceType } from './ai-services.types';
import { AiServiceItemComponent } from '../ai-service-item/ai-service-item.component';

@Component({
  standalone: true,
  imports: [AiServiceItemComponent, CommonModule, TrackingModule, UiModule],
  selector: 'app-ai-services',
  template: `
    @if (showHeader) {
      <p
        class="text-light text-body-24 tablet:text-title-32 font-bold mb-mid tablet:mb-large"
        i18n="AI services header"
        [flAnimateOnScroll]="true"
        [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
        [flAnimateOnScrollDuration]="0.3"
      >
        Or try a
        <span class="Header">refundable</span>
        obligation-free consultation before you buy a service.
      </p>
    }
    <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
      @for (
        service of services;
        track trackByType($index, service);
        let isFirst = $first
      ) {
        <fl-col
          class="mb-mid"
          [col]="12"
          [colDesktopSmall]="4"
          [flAnimateOnScroll]="true"
          [flAnimateOnScrollBehavior]="AnimationBehavior.FADE"
          [flAnimateOnScrollDuration]="0.3"
        >
          <app-ai-service-item
            [filteredAiServiceOfferings$]="filteredAiServiceOfferings$"
            [isHomePage]="isHomePage"
            [service]="service"
            [isServiceOfferingsCollectionReady$]="
              isServiceOfferingsCollectionReady$
            "
          ></app-ai-service-item>
        </fl-col>
      }
    </fl-grid>
  `,
  styleUrls: ['./ai-services.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiServicesComponent {
  AnimationBehavior = AnimationBehavior;
  VerticalAlignment = VerticalAlignment;

  @Input() isHomePage = false;
  @Input() showHeader = true;
  @Input() filteredAiServiceOfferings$: Observable<readonly ServiceOffering[]>;
  @Input() isServiceOfferingsCollectionReady$: Observable<boolean>;

  services: readonly Service[] = [
    {
      header: $localize`AI Lead Generator`,
      headerIcon: 'ui-data-loss-prevention',
      description: $localize`Our AI voice agent is designed to transform your business's approach to lead generation, customer service follow-ups, confirmation calls, and appointment setting.`,
      src: 'back-to-work/ai-services/v2/lead-generator.png',
      type: ServiceType.LEAD_GENERATOR_OLD_DESIGN,
      link: '/service/artificial_intelligence/ai-lead-generator',
      videoSrc: 'https://www.f-cdn.com/assets/upload/AILeadGenDemoV2.mp4',
    },
    {
      header: $localize`AI Appointment Setter`,
      headerIcon: 'ui-calendar-v2',
      description: $localize`Designed to handle inbound service calls efficiently, our AI voice agent can answer inquiries, schedule appointments, process payments, and send confirmations.`,
      src: 'back-to-work/ai-services/v2/appointment-setter.png',
      type: ServiceType.APPOINTMENT_SETTER_OLD_DESIGN,
      link: '/service/artificial_intelligence/ai-appointment-setter',
      videoSrc: 'https://www.f-cdn.com/assets/upload/AIApptSetterDemoV2.mp4',
    },
    {
      header: $localize`AI Support Agent`,
      headerIcon: 'ui-headset-mic',
      description: $localize`Designed to handle a high volume of inquiries efficiently, our AI agent can answer common questions, resolve basic issues, and seamlessly transfer complex problems to human agents.`,
      src: 'back-to-work/ai-services/v2/support-agent.png',
      type: ServiceType.SUPPORT_AGENT_OLD_DESIGN,
      link: '/service/artificial_intelligence/ai-support-agent',
      videoSrc: 'https://www.f-cdn.com/assets/upload/AISupportAgentDemoV2.mp4',
    },
  ];

  trackByType(_: number, service: Service): string {
    return service.type;
  }
}
